import { useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as StoreReview from 'expo-store-review';
import { Platform, Linking } from 'react-native';
import { useActionSheet } from '@expo/react-native-action-sheet';
import useTranslate from '@hooks/useTranslate';

const STORAGE_KEYS = {
  NEVER_SHOW: 'kng-review-never-show',
  LAST_SHOWN: 'kng-review-last-shown',
  FIRST_ACTION: 'kng-review-first-action',
};

const DAYS_BEFORE_FIRST_PROMPT = 7;
const DAYS_BETWEEN_PROMPTS = 30;
const DELAY_MS = 500;

const STORE_URLS = {
  ios: 'itms-apps://itunes.apple.com/app/viewContentsUserReviews/id1626866830?action=write-review',
  android: 'market://details?id=fr.homemeethome.app&showAllReviews=true',
};

function daysSince(timestamp) {
  return (Date.now() - parseInt(timestamp, 10)) / (1000 * 60 * 60 * 24);
}

async function checkShouldShow() {
  const neverShow = await AsyncStorage.getItem(STORAGE_KEYS.NEVER_SHOW);
  if (neverShow === 'true') return false;

  const firstAction = await AsyncStorage.getItem(STORAGE_KEYS.FIRST_ACTION);
  if (!firstAction) {
    await AsyncStorage.setItem(STORAGE_KEYS.FIRST_ACTION, Date.now().toString());
    return false;
  }

  if (daysSince(firstAction) < DAYS_BEFORE_FIRST_PROMPT) return false;

  const lastShown = await AsyncStorage.getItem(STORAGE_KEYS.LAST_SHOWN);
  if (lastShown && daysSince(lastShown) < DAYS_BETWEEN_PROMPTS) return false;

  return true;
}

async function openNativeReview() {
  const isAvailable = await StoreReview.isAvailableAsync();
  if (isAvailable) {
    await StoreReview.requestReview();
    return;
  }

  const url = Platform.select(STORE_URLS);
  if (url) {
    await Linking.openURL(url);
  }
}

function markAsShown() {
  AsyncStorage.setItem(STORAGE_KEYS.LAST_SHOWN, Date.now().toString());
}

function markAsNeverShow() {
  AsyncStorage.setItem(STORAGE_KEYS.NEVER_SHOW, 'true');
}

async function handleRate() {
  markAsShown();
  await openNativeReview();
}

export default function useReviewPrompt() {
  const { t } = useTranslate();
  const { showActionSheetWithOptions } = useActionSheet();

  const requestReview = useCallback(() => {
    setTimeout(async () => {
      try {
        if ('web' === Platform.OS) {
          return;
        }

        const shouldShow = await checkShouldShow();
        if (!shouldShow) return;

        showActionSheetWithOptions(
          {
            title: t('hooks.useReviewPrompt.title'),
            message: t('hooks.useReviewPrompt.description'),
            options: [
              t('hooks.useReviewPrompt.rateButton'),
              t('hooks.useReviewPrompt.alreadyRatedLink'),
              t('hooks.useReviewPrompt.laterButton'),
            ],
            cancelButtonIndex: 2,
          },
          (index) => {
            if (0 === index) {
              handleRate();
            } else if (1 === index) {
              markAsNeverShow();
            } else {
              markAsShown();
            }
          }
        );
      } catch {
        // Silently fail — review prompt is non-critical
      }
    }, DELAY_MS);
  }, [showActionSheetWithOptions, t]);

  return { requestReview };
}
